import { Stack, Tooltip, TooltipProps, Typography, TypographyProps } from '@mui/material';
import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useTruncatedText } from './useTruncatedText';

export interface TruncatedLinksProps {
  items: Array<{ name: string; link: { to: string; target?: string } }>;
  separator?: string;
  typographyProps?: TypographyProps;
  tooltipTitle?: React.ReactNode;
  tooltipProps?: Omit<TooltipProps, 'children' | 'title'> & { title?: React.ReactNode };
}

const COMMON_LINK_STYLES = {
  '& a': { color: 'common.grey2', textDecoration: 'none' },
  '& a:hover': { textDecoration: 'underline' },
};

export const TruncatedLinks: FC<TruncatedLinksProps> = ({
  items,
  separator = ', ',
  typographyProps,
  tooltipProps,
}) => {
  const { ref, containerRef, hiddenCount } = useTruncatedText({ itemsCount: items.length });

  const { title: tooltipTitle, ...restTooltipProps } = tooltipProps ?? {};

  const fullLinks = useMemo(() => {
    return (
      <Stack sx={COMMON_LINK_STYLES}>
        {items.map(({ name, link }, i) => (
          <Typography
            key={`name-${i}`}
            component={Link}
            to={link.to}
            target={link.target}
            {...typographyProps}
          >
            {name}
          </Typography>
        ))}
      </Stack>
    );
  }, [items, typographyProps]);

  const title = tooltipTitle ?? fullLinks;

  return (
    <Tooltip title={hiddenCount > 0 ? title : ''} {...restTooltipProps}>
      <Stack ref={ref} direction="row" alignItems="flex-end">
        <Typography
          ref={containerRef}
          noWrap
          {...typographyProps}
          sx={{ ...COMMON_LINK_STYLES, ...typographyProps?.sx }}
        >
          {items.map(({ name, link }, i, array) => (
            <Typography
              key={`name-${i}`}
              component={Link}
              to={link.to}
              target={link.target}
              {...typographyProps}
            >
              {i < array.length - 1 ? `${name}${separator}` : name}
            </Typography>
          ))}
        </Typography>
        {hiddenCount > 0 && (
          <Typography
            component="span"
            {...typographyProps}
            sx={{ ml: 0.5, ...typographyProps?.sx }}
          >
            {` +${hiddenCount}`}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
};
